<template>
    <modal :dismissable="true" @dismiss="dismiss" class="search-results-modal" v-if="showModal">
        <IconTextbox v-model="searchText" iconType="search" class="mt-search-input" />
        <TabbedViewTabs :tabs="resultFilters" :active.sync="activeFilter" class="light" />
        <div class="tab-contents loading-container">
            <CompanySearchResultsPanel
                v-if="companySearchResultsVisible"
                :companies="results.companies"
                :maxItems="resultLimit"
                :showViewAll="activeFilter !== 'Companies'"
                @navigating="close"
                @viewAll="viewAllCompanies"
            />

            <ExecutiveSearchResultsPanel
                v-if="executiveSearchResultsVisible"
                :executives="results.executives"
                :maxItems="resultLimit"
                :showViewAll="activeFilter !== 'Executives'"
                @navigating="close"
                @viewAll="viewAllExecutives"
            />

            <InterviewSearchResultPanel
                v-if="interviewSearchResultsVisible"
                :interviews="results.interviews"
                :maxItems="resultLimit"
                :showViewAll="activeFilter !== 'Interviews'"
                :query="searchText"
                @navigating="close"
                @viewAll="viewAllInterviews"
            />

            <loading :active="loading" :is-full-page="false" />
        </div>
    </modal>
</template>
<script>
import SearchService from '@/services/searchService'
import debounce from 'debounce-promise'

import TabbedViewTabs from '@/components/Tabs/tabbed-view-tabs'
import IconTextbox from '../shared/icon-textbox.vue'
import InterviewSearchResultPanel from './InterviewSearchResultPanel.vue'
import CompanySearchResultsPanel from './CompanySearchResultsPanel.vue'
import ExecutiveSearchResultsPanel from './ExecutiveSearchResultsPanel.vue'

const SEARCH_PARAM = 'site.search'

const FILTER_ALL = 'All'
const FILTER_EXECUTIVES = 'Executives'
const FILTER_COMPANIES = 'Companies'
const FILTER_INTERVIEWS = 'Interviews'

export default {
    components: {
        TabbedViewTabs,
        IconTextbox,
        InterviewSearchResultPanel,
        CompanySearchResultsPanel,
        ExecutiveSearchResultsPanel,
    },
    mounted() {
        window.addEventListener('popstate', this.popState)

        if (this.$route.query[SEARCH_PARAM]) {
            this.open = true
            this.searchText = this.$route.query[SEARCH_PARAM]
        }
    },
    beforeDestroy() {
        window.removeEventListener('popstate', this.popState)
    },
    watch: {
        searchText() {
            this.updateQueryString()
        },
        showModal(to) {
            if (to) {
                this.updateQueryString()
            }
        },
    },
    asyncComputed: {
        results: {
            get() {
                return this.search(this.searchText, this.entityTypes)
            },
            default() {
                return {
                    executives: [],
                    companies: [],
                    interviews: [],
                }
            },
        },
    },
    computed: {
        activeFilter: {
            get() {
                return this.$store.getters.searchModal.tab ?? FILTER_ALL
            },
            set(tab) {
                this.$store.getters.searchModal.tab = tab
            },
        },
        companySearchResultsVisible() {
            return this.activeFilter === FILTER_ALL || this.activeFilter === FILTER_COMPANIES
        },
        entityTypes() {
            return null
        },
        executiveSearchResultsVisible() {
            return this.activeFilter === FILTER_ALL || this.activeFilter === FILTER_EXECUTIVES
        },
        interviewSearchResultsVisible() {
            return this.activeFilter === FILTER_ALL || this.activeFilter === FILTER_INTERVIEWS
        },
        loading() {
            return this.$asyncComputed.results.updating
        },
        resultFilters() {
            return [FILTER_ALL, FILTER_EXECUTIVES, FILTER_COMPANIES, FILTER_INTERVIEWS]
        },
        resultLimit() {
            return this.activeFilter === FILTER_ALL ? 5 : 100
        },
        searchText: {
            get() {
                return this.$store.getters.searchModal.search
            },
            set(value) {
                this.$store.getters.searchModal.search = value
            },
        },
        showModal: {
            get() {
                return this.$store.getters.searchModal.open
            },
            set(open) {
                this.$store.getters.searchModal.open = open
            },
        },
    },
    methods: {
        close() {
            this.showModal = false
        },
        dismiss() {
            this.close()

            // we actively closed the modal; update the history
            let query = structuredClone(this.$route.query)
            delete query[SEARCH_PARAM]
            const { href } = this.$router.resolve({ query })
            window.history.replaceState(null, null, href)
        },
        popState(event) {
            if (event.state?.[SEARCH_PARAM]) {
                this.$store.getters.searchModal.open = true
            } else {
                this.$store.getters.searchModal.open = false
            }
        },
        search: debounce(SearchService.search, 500),
        updateQueryString() {
            if (!this.showModal) {
                return
            }

            let query = structuredClone(this.$route.query)

            // showing the modal; add the current searchText to the url
            // and call replaceState (if there was already a parameter in there)
            // or call pushState (if there was not already a parameter in there)
            // this should create ONE history entry for this instance of
            // the search modal being open
            query[SEARCH_PARAM] = this.searchText

            const { href } = this.$router.resolve({ query })

            if (this.$route.query[SEARCH_PARAM]) {
                window.history.replaceState({ [SEARCH_PARAM]: this.searchText }, null, href)
            } else {
                window.history.pushState({ [SEARCH_PARAM]: this.searchText }, null, href)
            }
        },
        viewAllCompanies() {
            this.activeFilter = FILTER_COMPANIES
        },
        viewAllExecutives() {
            this.activeFilter = FILTER_EXECUTIVES
        },
        viewAllInterviews() {
            this.activeFilter = FILTER_INTERVIEWS
        },
    },
}
</script>

<style>
.search-results-modal .modal {
    max-height: 100vh;
    padding: 0;
    width: 960px;
    height: 90vh;
    padding: 16px;

    .modal-body {
        display: flex;
        flex-direction: column;

        .tab-contents {
            flex-grow: 1;
            height: 1%;
            margin-top: 40px;
            padding-right: 16px;
            overflow-y: auto;
        }
    }

    .search-result-panel + .search-result-panel {
        margin-top: 40px;
    }

    .modal-header {
        display: flex;
        flex-direction: column;
        background-color: white;
    }
}

@media (min-width: 960.1px) {
    .search-results-modal .modal .modal-header {
        display: none;
    }
}

@media (max-width: 960px) {
    .search-results-modal {
        .modal {
            height: 100vh;

            padding: 24px;
            margin: 0px;
        }
    }
}
</style>
